import React, { useEffect, useState } from 'react';
import { istDate, istDateTime } from '../lib/ist';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Copy, CheckCircle2, X, BarChart3, MapPin } from 'lucide-react';
import { API, useAuth, canEdit } from '../App';
import ScrollSentinel from '../components/ScrollSentinel';

const DEFAULT_FORM = {
  code: '', display_name: '', type: 'percent', value: 50,
  notes: '', max_uses_total: '', max_uses_per_customer: 1,
  valid_until: '', min_amount: '',
  scope_mode: 'all', kiosk_ids: [],
};
const DEFAULT_BATCH_FORM = {
  prefix: '', count: 10, type: 'percent', value: 50,
  notes: '', max_uses_total: 1, max_uses_per_customer: 1,
  scope_mode: 'all', kiosk_ids: [],
};

const Vouchers = () => {
  const { token, user } = useAuth();
  const editable = canEdit(user, 'vouchers');
  const [vouchers, setVouchers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all'); // all | global | specific | kioskId
  const [showCreate, setShowCreate] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Create form state
  const [form, setForm] = useState(DEFAULT_FORM);
  const [batchForm, setBatchForm] = useState(DEFAULT_BATCH_FORM);
  const [batchResult, setBatchResult] = useState(null);

  const kioskNameById = React.useMemo(() => {
    const m = {};
    kiosks.forEach(k => { m[k.id] = k.name || k.id; });
    return m;
  }, [kiosks]);

  const voucherParams = () => {
    const p = { page: 1, page_size: 50 };
    if (filter === 'active') p.active = true;
    if (filter === 'inactive') p.active = false;
    if (search.trim()) p.search = search.trim();
    return p;
  };

  // Sequence guard: a filter/search refetch invalidates any in-flight page append.
  const fetchSeq = React.useRef(0);

  const fetchAll = async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const [vRes, aRes, kRes] = await Promise.all([
        axios.get(`${API}/vouchers`, { headers: { Authorization: `Bearer ${token}` }, params: voucherParams() }),
        axios.get(`${API}/vouchers-analytics/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/kiosks`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (seq !== fetchSeq.current) return;
      setVouchers(vRes.data.vouchers || []);
      setPage(1);
      setHasMore(50 < (vRes.data.total || 0));
      setAnalytics(aRes.data);
      setKiosks(kRes.data || []);
    } catch (e) {
      toast.error('Failed to load vouchers');
    }
    setLoading(false);
  };

  const loadMoreVouchers = async () => {
    if (loadingMore || !hasMore || loading) return;
    const seq = fetchSeq.current;
    setLoadingMore(true);
    try {
      const params = { ...voucherParams(), page: page + 1 };
      const r = await axios.get(`${API}/vouchers`, { headers: { Authorization: `Bearer ${token}` }, params });
      if (seq !== fetchSeq.current) { setLoadingMore(false); return; }
      const rows = r.data.vouchers || [];
      setVouchers((prev) => {
        const seen = new Set(prev.map((v) => v.id));
        return [...prev, ...rows.filter((v) => !seen.has(v.id))];
      });
      setPage(page + 1);
      setHasMore((page + 1) * 50 < (r.data.total || 0));
    } catch (e) { /* silent */ }
    setLoadingMore(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => { fetchAll(); }, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search]);

  const createVoucher = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    if (form.type !== 'free' && (!form.value || form.value <= 0)) { toast.error('Value must be > 0'); return; }
    if (form.scope_mode === 'specific' && form.kiosk_ids.length === 0) {
      toast.error('Pick at least one booth or switch to "All booths"'); return;
    }
    const payload = {
      code: form.code.trim().toUpperCase(),
      display_name: form.display_name || form.code.trim().toUpperCase(),
      type: form.type,
      value: form.type === 'free' ? 0 : Number(form.value),
      notes: form.notes,
      scope: {
        max_uses_total: form.max_uses_total ? Number(form.max_uses_total) : null,
        max_uses_per_customer: Number(form.max_uses_per_customer || 0),
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        min_amount: form.min_amount ? Number(form.min_amount) : null,
        kiosk_ids: form.scope_mode === 'specific' ? form.kiosk_ids : null,
      },
    };
    try {
      await axios.post(`${API}/vouchers`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Voucher ${payload.code} created`);
      setShowCreate(false);
      setForm(DEFAULT_FORM);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
  };

  const createBatch = async () => {
    if (!batchForm.count || batchForm.count < 1) { toast.error('Count must be > 0'); return; }
    if (batchForm.scope_mode === 'specific' && batchForm.kiosk_ids.length === 0) {
      toast.error('Pick at least one booth or switch to "All booths"'); return;
    }
    const payload = {
      prefix: batchForm.prefix.trim().toUpperCase(),
      count: Number(batchForm.count),
      type: batchForm.type,
      value: batchForm.type === 'free' ? 0 : Number(batchForm.value),
      notes: batchForm.notes,
      scope: {
        max_uses_total: Number(batchForm.max_uses_total),
        max_uses_per_customer: Number(batchForm.max_uses_per_customer),
        kiosk_ids: batchForm.scope_mode === 'specific' ? batchForm.kiosk_ids : null,
      },
    };
    try {
      const res = await axios.post(`${API}/vouchers/batch`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setBatchResult(res.data);
      toast.success(`Created ${res.data.created} vouchers`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deactivate = async (v) => {
    if (!window.confirm(`Deactivate voucher ${v.code}? Existing redemptions are preserved.`)) return;
    try {
      await axios.delete(`${API}/vouchers/${v.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deactivated');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopiedCode(code); setTimeout(() => setCopiedCode(''), 1500); } catch {}
  };

  const exportCsv = () => {
    if (!batchResult) return;
    const csv = ['code'].concat(batchResult.vouchers.map(v => v.code)).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vouchers-${batchResult.batch_id.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderType = (v) => {
    if (v.type === 'free') return <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-800">FREE</span>;
    if (v.type === 'percent') return <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">{v.value}% off</span>;
    return <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">₹{v.value} off</span>;
  };

  const filteredVouchers = React.useMemo(() => {
    if (scopeFilter === 'all') return vouchers;
    return vouchers.filter(v => {
      const ids = v.scope?.kiosk_ids || [];
      if (scopeFilter === 'global') return ids.length === 0;
      if (scopeFilter === 'specific') return ids.length > 0;
      // exact kiosk id
      return ids.includes(scopeFilter);
    });
  }, [vouchers, scopeFilter]);

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-charcoal-900">Vouchers</h1>
          <p className="text-sm text-charcoal-800/60 mt-1">Discount codes, comp tickets, and pre-paid event allowances</p>
        </div>
        {editable && (
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(true)} data-testid="voucher-create-btn"
            className="bg-charcoal-900 text-cream-50 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-charcoal-800">
            <Plus className="w-4 h-4" /> Create voucher
          </button>
          <button onClick={() => { setShowBatch(true); setBatchResult(null); }} data-testid="voucher-batch-btn"
            className="border border-charcoal-900/20 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-cream-50">
            <Plus className="w-4 h-4" /> Bulk generate
          </button>
        </div>
        )}
      </div>

      {/* Analytics summary */}
      {analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Active vouchers" value={analytics.total_active_vouchers} />
          <Stat label="Total redemptions" value={analytics.total_redemptions} />
          <Stat label="Amount saved" value={`₹${analytics.total_amount_saved}`} />
          <Stat label="Revenue via codes" value={`₹${analytics.total_voucher_revenue ?? 0}`} />
          <Stat label="Top code" value={(analytics.codes?.[0] || analytics.top_codes?.[0])?.code || '—'} />
        </div>
      )}

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'active', 'inactive'].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`voucher-filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-sm capitalize ${filter === f ? 'bg-charcoal-900 text-cream-50' : 'bg-cream-100 text-charcoal-800/70 hover:bg-cream-200'}`}>
            {f}
          </button>
        ))}
        <span className="text-charcoal-800/30 px-1">·</span>
        <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} data-testid="voucher-scope-filter"
          className="px-3 py-1.5 rounded-full text-sm border border-charcoal-900/10 bg-cream-100 text-charcoal-800/80">
          <option value="all">All scopes</option>
          <option value="global">Global (all booths)</option>
          <option value="specific">Booth-specific</option>
          {kiosks.map(k => (
            <option key={k.id} value={k.id}>Applies at: {k.name}</option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search code…"
          data-testid="voucher-search-input"
          className="px-3 py-1.5 rounded-full text-sm border border-charcoal-900/10 bg-white w-40 focus:outline-none focus:border-charcoal-900/30"
        />
        <button onClick={fetchAll} className="ml-auto text-sm text-charcoal-800/60 hover:text-charcoal-900 flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Voucher table */}
      <div className="card-dashboard p-0 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-charcoal-800/40">Loading...</div>
        ) : filteredVouchers.length === 0 ? (
          <div className="text-center py-12 text-charcoal-800/40">
            {vouchers.length === 0 ? 'No vouchers yet — create your first above.' : 'No vouchers match this scope filter.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vouchers-table">
              <thead className="bg-cream-50">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Code</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Type</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Applies to</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Uses</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Created</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Valid until</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-900/5">
                {filteredVouchers.map(v => (
                  <tr key={v.id} className="hover:bg-cream-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-charcoal-900">{v.code}</span>
                        <button onClick={() => copyCode(v.code)} className="text-charcoal-800/40 hover:text-charcoal-900">
                          {copiedCode === v.code ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      {v.display_name !== v.code && <div className="text-xs text-charcoal-800/50">{v.display_name}</div>}
                    </td>
                    <td className="px-4 py-3">{renderType(v)}</td>
                    <td className="px-4 py-3">{renderScope(v, kioskNameById)}</td>
                    <td className="px-4 py-3 text-charcoal-800/70">
                      {v.uses}{v.scope?.max_uses_total ? <span className="text-charcoal-800/40"> / {v.scope.max_uses_total}</span> : ''}
                    </td>
                    <td className="px-4 py-3 text-xs text-charcoal-800/50">
                      {istDate(v.created_at)}
                      <div className="text-[10px]">by {v.created_by_username}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-charcoal-800/60">
                      {v.scope?.valid_until ? istDate(v.scope.valid_until) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {v.active ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">Active</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {v.active && editable && (
                        <button onClick={() => deactivate(v)} data-testid={`voucher-delete-${v.code}`}
                          className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ScrollSentinel
          onVisible={loadMoreVouchers}
          disabled={loading || loadingMore || !hasMore}
          testid="vouchers-scroll-sentinel"
        />
        {loadingMore && <div className="text-center py-3 text-xs text-charcoal-800/40">Loading more vouchers…</div>}
      </div>

      {/* Top codes */}
      {(analytics?.codes?.length > 0 || analytics?.top_codes?.length > 0) && (
        <div className="card-dashboard p-0 overflow-hidden" data-testid="voucher-analytics-table">
          <h3 className="font-serif text-lg px-4 py-3 border-b border-charcoal-900/5 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Code performance
          </h3>
          <table className="w-full text-sm">
            <thead className="bg-cream-50">
              <tr className="text-left">
                <th className="px-4 py-2.5 text-xs font-medium text-charcoal-800/60 uppercase">Code</th>
                <th className="px-4 py-2.5 text-xs font-medium text-charcoal-800/60 uppercase">Redemptions</th>
                <th className="px-4 py-2.5 text-xs font-medium text-charcoal-800/60 uppercase">Revenue</th>
                <th className="px-4 py-2.5 text-xs font-medium text-charcoal-800/60 uppercase">Discount given</th>
                <th className="px-4 py-2.5 text-xs font-medium text-charcoal-800/60 uppercase">Last used</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal-900/5">
              {(analytics.codes || analytics.top_codes).map(c => (
                <tr key={c.code} className="hover:bg-cream-50" data-testid={`voucher-analytics-row-${c.code}`}>
                  <td className="px-4 py-2.5 font-mono font-medium">{c.code}</td>
                  <td className="px-4 py-2.5">{c.redemptions}{c.orders != null && c.orders !== c.redemptions ? ` (${c.orders} orders)` : ''}</td>
                  <td className="px-4 py-2.5 text-green-700 font-medium">₹{c.revenue ?? 0}</td>
                  <td className="px-4 py-2.5 text-charcoal-800/60">₹{c.saved ?? 0}</td>
                  <td className="px-4 py-2.5 text-xs text-charcoal-800/50">{c.last_used ? istDateTime(c.last_used) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <Modal title="Create voucher" onClose={() => setShowCreate(false)}>
          <Field label="Code (uppercase)">
            <input data-testid="voucher-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WEDDING50" className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg font-mono uppercase" />
          </Field>
          <Field label="Display name (optional)">
            <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Wedding 50% off" className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select data-testid="voucher-form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg">
                <option value="percent">% off</option>
                <option value="flat_off">Flat ₹ off</option>
                <option value="free">Free (skip payment)</option>
              </select>
            </Field>
            {form.type !== 'free' && (
              <Field label={form.type === 'percent' ? 'Percent' : 'Rupees'}>
                <input data-testid="voucher-form-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
              </Field>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max total uses (blank = ∞)">
              <input type="number" value={form.max_uses_total} onChange={(e) => setForm({ ...form, max_uses_total: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
            </Field>
            <Field label="Max per customer (0 = unlimited)">
              <input type="number" value={form.max_uses_per_customer} onChange={(e) => setForm({ ...form, max_uses_per_customer: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valid until (optional)">
              <input type="datetime-local" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
            </Field>
            <Field label="Minimum order ₹ (optional)">
              <input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
            </Field>
          </div>
          <ScopePicker
            mode={form.scope_mode}
            kioskIds={form.kiosk_ids}
            kiosks={kiosks}
            onChange={(mode, ids) => setForm({ ...form, scope_mode: mode, kiosk_ids: ids })}
            testIdPrefix="voucher-form"
          />
          <Field label="Notes (internal)">
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Manish wedding 22-Feb" className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
          </Field>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-charcoal-800/60">Cancel</button>
            <button onClick={createVoucher} data-testid="voucher-form-submit" className="bg-charcoal-900 text-cream-50 px-4 py-2 rounded-lg">Create</button>
          </div>
        </Modal>
      )}

      {/* Batch modal */}
      {showBatch && (
        <Modal title="Bulk-generate vouchers" onClose={() => { setShowBatch(false); setBatchResult(null); }}>
          {!batchResult ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prefix (optional)">
                  <input value={batchForm.prefix} onChange={(e) => setBatchForm({ ...batchForm, prefix: e.target.value.toUpperCase() })} placeholder="EVENT" className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg font-mono uppercase" />
                </Field>
                <Field label="How many?">
                  <input type="number" min="1" max="1000" value={batchForm.count} onChange={(e) => setBatchForm({ ...batchForm, count: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <select value={batchForm.type} onChange={(e) => setBatchForm({ ...batchForm, type: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg">
                    <option value="percent">% off</option>
                    <option value="flat_off">Flat ₹ off</option>
                    <option value="free">Free</option>
                  </select>
                </Field>
                {batchForm.type !== 'free' && (
                  <Field label="Value">
                    <input type="number" value={batchForm.value} onChange={(e) => setBatchForm({ ...batchForm, value: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
                  </Field>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Max uses per code">
                  <input type="number" value={batchForm.max_uses_total} onChange={(e) => setBatchForm({ ...batchForm, max_uses_total: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
                </Field>
                <Field label="Max per customer">
                  <input type="number" value={batchForm.max_uses_per_customer} onChange={(e) => setBatchForm({ ...batchForm, max_uses_per_customer: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
                </Field>
              </div>
              <ScopePicker
                mode={batchForm.scope_mode}
                kioskIds={batchForm.kiosk_ids}
                kiosks={kiosks}
                onChange={(mode, ids) => setBatchForm({ ...batchForm, scope_mode: mode, kiosk_ids: ids })}
                testIdPrefix="voucher-batch"
              />
              <Field label="Notes (shared across all codes)">
                <input value={batchForm.notes} onChange={(e) => setBatchForm({ ...batchForm, notes: e.target.value })} className="w-full px-3 py-2 border border-charcoal-900/10 rounded-lg" />
              </Field>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowBatch(false)} className="px-4 py-2 text-charcoal-800/60">Cancel</button>
                <button onClick={createBatch} className="bg-charcoal-900 text-cream-50 px-4 py-2 rounded-lg">Generate</button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-green-700 bg-green-50 rounded-lg p-3 mb-3">
                ✓ Created {batchResult.created} codes. Batch id: <span className="font-mono text-xs">{batchResult.batch_id.slice(0, 8)}</span>
              </div>
              <div className="max-h-72 overflow-y-auto bg-cream-50 rounded-lg p-3 font-mono text-xs">
                {batchResult.vouchers.map(v => <div key={v.id}>{v.code}</div>)}
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={exportCsv} className="border border-charcoal-900/20 px-4 py-2 rounded-lg">Download CSV</button>
                <button onClick={() => { setShowBatch(false); setBatchResult(null); setBatchForm(DEFAULT_BATCH_FORM); }} className="bg-charcoal-900 text-cream-50 px-4 py-2 rounded-lg">Done</button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="card-dashboard">
    <div className="text-xs uppercase text-charcoal-800/50 tracking-wider">{label}</div>
    <div className="font-serif text-2xl mt-1">{value}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1 mb-2">
    <label className="text-xs text-charcoal-800/60">{label}</label>
    {children}
  </div>
);

const renderScope = (v, kioskNameById) => {
  const ids = v.scope?.kiosk_ids || [];
  if (ids.length === 0) {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-cream-100 text-charcoal-800/70 flex items-center gap-1 w-fit">
        <MapPin className="w-3 h-3" /> All booths
      </span>
    );
  }
  if (ids.length === 1) {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800 flex items-center gap-1 w-fit"
        title={kioskNameById[ids[0]] || ids[0]}>
        <MapPin className="w-3 h-3" /> {kioskNameById[ids[0]] || ids[0].slice(0, 8)}
      </span>
    );
  }
  const names = ids.map(id => kioskNameById[id] || id).join(', ');
  return (
    <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800 flex items-center gap-1 w-fit" title={names}>
      <MapPin className="w-3 h-3" /> {ids.length} booths
    </span>
  );
};

const ScopePicker = ({ mode, kioskIds, kiosks, onChange, testIdPrefix }) => {
  const toggleKiosk = (id) => {
    if (kioskIds.includes(id)) {
      onChange('specific', kioskIds.filter(k => k !== id));
    } else {
      onChange('specific', [...kioskIds, id]);
    }
  };
  return (
    <div className="space-y-2 mb-2 border-t border-charcoal-900/10 pt-3">
      <label className="text-xs text-charcoal-800/60 flex items-center gap-1">
        <MapPin className="w-3.5 h-3.5" /> Applies to
      </label>
      <div className="flex gap-2">
        <button type="button"
          onClick={() => onChange('all', [])}
          data-testid={`${testIdPrefix}-scope-all`}
          className={`flex-1 px-3 py-2 rounded-lg text-sm border ${mode === 'all'
            ? 'bg-charcoal-900 text-cream-50 border-charcoal-900'
            : 'bg-cream-50 text-charcoal-800/70 border-charcoal-900/10 hover:bg-cream-100'}`}>
          All booths
        </button>
        <button type="button"
          onClick={() => onChange('specific', kioskIds)}
          data-testid={`${testIdPrefix}-scope-specific`}
          className={`flex-1 px-3 py-2 rounded-lg text-sm border ${mode === 'specific'
            ? 'bg-charcoal-900 text-cream-50 border-charcoal-900'
            : 'bg-cream-50 text-charcoal-800/70 border-charcoal-900/10 hover:bg-cream-100'}`}>
          Specific booths
        </button>
      </div>
      {mode === 'specific' && (
        <div className="border border-charcoal-900/10 rounded-lg p-2 max-h-44 overflow-y-auto space-y-1 bg-cream-50/50"
          data-testid={`${testIdPrefix}-kiosk-list`}>
          {kiosks.length === 0 ? (
            <div className="text-xs text-charcoal-800/40 px-2 py-3 text-center">No booths available.</div>
          ) : kiosks.map(k => (
            <label key={k.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-cream-100 cursor-pointer text-sm">
              <input type="checkbox"
                data-testid={`${testIdPrefix}-kiosk-${k.id}`}
                checked={kioskIds.includes(k.id)}
                onChange={() => toggleKiosk(k.id)}
                className="rounded" />
              <span className="flex-1">{k.name}</span>
              <span className="text-xs text-charcoal-800/40">{k.location || ''}</span>
            </label>
          ))}
          <div className="text-[10px] text-charcoal-800/50 px-2 pt-1">
            {kioskIds.length} of {kiosks.length} selected
          </div>
        </div>
      )}
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-cream-50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl">{title}</h2>
        <button onClick={onClose} className="text-charcoal-800/60 hover:text-charcoal-900"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default Vouchers;
